import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRecaptcha } from "@/lib/useRecaptcha";
import { toast } from "sonner";
import { meta } from "@/lib/metaPixel";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignup() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const { execute } = useRecaptcha();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("intent") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Persist intent across the register -> onboarding -> billing pathway
  useEffect(() => {
    if (intent === "pro_unlimited") {
      try { sessionStorage.setItem("dms_billing_intent", "pro_unlimited"); } catch (e) { console.debug("[register] sessionStorage set failed:", e?.name); }
    }
  }, [intent]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be 8+ characters"); return; }
    setBusy(true);
    try {
      const token = await execute("register");
      const u = await register(email, password, name, token);
      // Meta: fire CompleteRegistration ONCE per new user_id. Only the email/password
      // path lands here — Google OAuth registrations are handled in AuthCallback.
      if (u?.user_id) meta.completeRegistration(u.user_id);
      toast.success("Account created");
      // New users land straight on the dashboard so the tools and value are
      // visible immediately. Onboarding is now nudged via the Welcome card
      // on the Dashboard rather than gated as a forced next step.
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
          </div>
          <span className="font-heading font-bold text-[15px] text-[#0A2342]">Dive Media Strategist™</span>
        </Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="dms-label mb-3">Create account</div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-2">Build your Dive Brain.</h1>
            <p className="text-[#475569] mb-8">Nine quick questions. Everything is editable later.</p>

            <button data-testid="google-register-button" onClick={googleSignup} type="button" className="w-full dms-btn-ghost mb-4">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/></svg>
              Continue with Google
            </button>

            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]" /></div>
              <span className="relative bg-[#F8FAFC] px-3 text-[11px] tracking-[0.2em] text-[#94A3B8] uppercase">or with email</span>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="dms-label block mb-2">Name</label>
                <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="dms-input" placeholder="Your name" />
              </div>
              <div>
                <label className="dms-label block mb-2">Email</label>
                <input data-testid="register-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="dms-input" placeholder="you@ocean.com" />
              </div>
              <div>
                <label className="dms-label block mb-2">Password</label>
                <input data-testid="register-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="dms-input" placeholder="At least 8 characters" />
              </div>
              <button data-testid="register-submit-button" type="submit" disabled={busy} className="w-full dms-btn-primary">
                {busy ? "Creating…" : "Create account"}
              </button>
            </form>
            <p className="mt-6 text-sm text-[#475569]">
              Already onboard?{" "}
              <Link to="/login" data-testid="register-to-login" className="font-semibold text-[#1D4E89] hover:text-[#0A2342]">Sign in</Link>
            </p>
            <p className="mt-4 text-[11px] text-[#94A3B8]" data-testid="recaptcha-notice">
              This site is protected by reCAPTCHA and the Google
              {" "}<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>
              {" "}and <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a> apply.
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1682687982502-1529b3b33f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxzY3ViYSUyMGRpdmVyJTIwdW5kZXJ3YXRlciUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4MTQ2MTM0MHww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2342]/55 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Doctrine</div>
          <p className="mt-3 text-xl font-heading font-semibold leading-snug">Reach is engineered, not random.</p>
        </div>
      </div>
    </div>
  );
}
